/**
 * Permission-based access control utilities for frontend
 * 
 * This module provides utilities for checking user permissions in React components.
 * Permissions are extracted from JWT token and stored in user context.
 */

import { Permission } from './jwt';

export type { Permission };

export type UserRole = 'admin' | 'operations_manager' | 'full time member';

/**
 * Formats a role string to a human-readable display format
 * 
 * @param role - Role string (e.g., 'operations_manager', 'admin', 'full time member')
 * @returns Formatted role string (e.g., 'Operations Manager', 'Admin', 'Full Time Member')
 */
export function formatRole(role: string | undefined | null): string {
  if (!role) return '-';
  
  // Handle specific role mappings
  if (role === 'operations_manager') {
    return 'Operations Manager';
  }
  
  // For other roles, capitalize each word
  return role
    .split(/[\s_]+/) // Split on spaces or underscores
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Checks if a user can view all operational data (not just assigned items)
 * This includes admin and operations_manager roles
 * 
 * @param permissions - Array of user's permissions
 * @param role - User's role (for backward compatibility)
 * @returns true if user can view all operational data
 */
export function canViewAllData(permissions: Permission[] | undefined, role?: string | undefined): boolean {
  // Check permissions first (new system)
  if (permissions && permissions.length > 0) {
    return hasPermission(permissions, 'all.read');
  }
  
  // Fallback to role check for backward compatibility
  const normalizedRole = role?.toLowerCase().replace(/\s+/g, '_');
  return normalizedRole === 'admin' || normalizedRole === 'operations_manager';
}

/**
 * Maps a role to its effective permissions (frontend version matching backend logic)
 * 
 * @param role - User's role
 * @returns Array of permission strings
 */
export function getEffectivePermissions(role: UserRole | string): Permission[] {
  const normalizedRole = role.toLowerCase().replace(/\s+/g, '_');
  switch (normalizedRole) {
    case 'admin':
      // Admin has all permissions
      return [
        'all.read',
        'payments.read',
        'payments.manage'
      ];
    
    case 'operations_manager':
      // Operations manager can view all operational data but not payments
      return [
        'all.read'
      ];
    
    case 'full time member':
      // Full time members rely on pageAccess for orders hub; no global all.read by default
      return [];
    
    default:
      // Unknown role - no permissions
      return [];
  }
}

/**
 * Checks if a user has a specific permission
 * 
 * @param permissions - Array of user's permissions (from JWT/user context)
 * @param requiredPermission - Permission to check
 * @returns true if user has the permission
 */
export function hasPermission(permissions: Permission[] | undefined, requiredPermission: Permission): boolean {
  if (!permissions || permissions.length === 0) {
    return false;
  }
  return permissions.includes(requiredPermission);
}

/**
 * Checks if a user has any of the required permissions
 * 
 * @param permissions - Array of user's permissions
 * @param requiredPermissions - Array of permissions to check (OR logic)
 * @returns true if user has at least one of the required permissions
 */
export function hasAnyPermission(permissions: Permission[] | undefined, requiredPermissions: Permission[]): boolean {
  if (!permissions || permissions.length === 0) {
    return false;
  }
  return requiredPermissions.some(perm => permissions.includes(perm));
}

/**
 * Checks if a user has all of the required permissions
 * 
 * @param permissions - Array of user's permissions
 * @param requiredPermissions - Array of permissions to check (AND logic)
 * @returns true if user has all of the required permissions
 */
export function hasAllPermissions(permissions: Permission[] | undefined, requiredPermissions: Permission[]): boolean {
  if (!permissions || permissions.length === 0) {
    return false;
  }
  return requiredPermissions.every(perm => permissions.includes(perm));
}

